import React, { useEffect, useRef, useState } from "react";
import { Navbar, NavbarToggler } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, Link } from "react-router-dom";
import SideBar from "./SideBar";
import InitialsDDL from "./InitialsDDL";
import MPink from "../../src/assets/img/MPink.png";
import MainNavigation from "./MainNavigation";
import TimeExtendModal from "./Modals/TimeExtendModal";
import {
  EndTime,
  ExtendTime,
  getTableData,
  tableTimeEnd,
} from "../store/actions/cartAction";
import { getUserTableInfoAction } from "../store/actions/restaurantsAction";

const Header = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const { darkMode, color } = useSelector((state) => state.global);
  const { uid } = useSelector((state) => state.authUser);
  const { restaurantsDetail, userTableInfo } = useSelector(
    (state) => state.restaurants
  );
  const { allTablesData } = useSelector((state) => state.cart);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [showExpireBanner, setShowExpireBanner] = useState(false);
  const hasTableEndedRef = useRef(false);
  const sidebarRef = useRef(null);
  const dropdownRef = useRef(null);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const toggleModal = () => setIsModal((prev) => !prev);
  const allTables = Object.values(allTablesData?.allTables || {});
  const currentUserTable = allTables.find((table) => table.customerId === uid);
  const restaurantId =
    restaurantsDetail?.restaurantID || localStorage.getItem("restaurantId");

  const logo = ["/restaurants", "/order-history"].includes(location.pathname)
    ? MPink
    : restaurantsDetail?.restaurantLogo || MPink;

  const handleTimeAction = async (action) => {
    if (!currentUserTable) return;

    await dispatch(
      action(
        userTableInfo?.restaurantId,
        currentUserTable.tableNo,
        currentUserTable.customerId,
        allTablesData?.timer
      )
    );

    dispatch(getUserTableInfoAction(uid));
    dispatch(getTableData(restaurantId));
    setShowExpireBanner(false);
    setIsModal(false);
  };

  const shouldRenderHeader = ![
    "/auth",
    "/main",
    "/register",
    "/forget-password",
  ].includes(location.pathname);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleOutsideLanguage = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsActive(false);
      }
    };
    if (isActive) {
      window.addEventListener("click", handleOutsideLanguage);
    }
    return () => window.removeEventListener("click", handleOutsideLanguage);
  }, [isActive]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!currentUserTable?.expireAt) {
      setShowExpireBanner(false);
      return;
    }

    const checkUserTableExpiry = () => {
      const now = Date.now();
      const expiryMs = currentUserTable?.expireAt?.seconds * 1000;
      const warningStart = expiryMs - 5 * 60 * 1000;
      if (now >= expiryMs) {
        if (!hasTableEndedRef.current) {
          hasTableEndedRef.current = true;
          dispatch(
            tableTimeEnd(
              currentUserTable.restaurantId,
              currentUserTable.tableNumber,
              currentUserTable.customerId
            )
          );
        }
        setShowExpireBanner(false);
      } else if (now >= warningStart) {
        setShowExpireBanner(true);
      } else {
        setShowExpireBanner(false);
      }
    };

    checkUserTableExpiry();
    const interval = setInterval(checkUserTableExpiry, 50000);
    return () => clearInterval(interval);
  }, [
    allTablesData,
    currentUserTable?.customerId,
    currentUserTable?.expireAt,
    currentUserTable?.restaurantId,
    currentUserTable?.tableNumber,
    dispatch,
  ]);

  useEffect(() => {
    dispatch(getUserTableInfoAction(uid));
  }, [dispatch, uid]);

  return (
    <div
      className={`${darkMode ? "Header-dark" : "Header"} position-sticky top-0`}
      style={{ zIndex: 1000 }}
    >
      {shouldRenderHeader && (
        <>
          <Navbar
            className={`fixed-top d-flex justify-content-between align-items-center ${
              darkMode ? "bg-dark text-white" : "bg-white"
            } py-2`}
            style={{
              zIndex: 1000,
              backgroundColor: scrolled
                ? darkMode
                  ? "#ffffff"
                  : "#1a1a1a"
                : undefined,
              color: scrolled ? (darkMode ? "#000" : "#fff") : undefined,
              boxShadow: scrolled
                ? darkMode
                  ? "0 2px 6px rgba(255, 255, 255, 0.25)"
                  : "0 2px 6px rgba(0, 0, 0, 0.15)"
                : "none",
              transition: "all 0.3s ease-in-out",
            }}
          >
            <div
              className="d-flex align-items-center"
              style={{ minWidth: 100 }}
            >
              {uid ? (
                <NavbarToggler onClick={toggleSidebar} className="me-2" />
              ) : (
                <MainNavigation title={""} />
              )}
            </div>

            <div className="flex-grow-1 text-center">
              <img
                src={logo}
                className="img-fluid"
                alt="logo"
                style={{ maxHeight: "40px" }}
              />
            </div>

            <div
              className="d-flex align-items-center justify-content-end"
              style={{ minWidth: 100 }}
            >
              <InitialsDDL />
            </div>

            {showExpireBanner && (
              <div className="extend-bar-container position-relative w-100 py-1">
                <div className="extend-bar-outer">
                  <div
                    className="extend-bar-inner"
                    style={{
                      fontSize: "15px",
                      fontWeight: 600,
                      color: darkMode ? "#fff" : "#212121",
                    }}
                  >
                    ⚠️ Your table reservation is about to expire at{" "}
                    {userTableInfo?.restaurantName || "restaurant"}{" "}
                    <Link
                      onClick={toggleModal}
                      className="ms-3 text-decoration-underline"
                      style={{
                        color,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      ⏳ Extend Time Now
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </Navbar>

          <div style={{ height: showExpireBanner ? "100px" : "75px" }} />
        </>
      )}

      <SideBar
        toggleSidebar={toggleSidebar}
        sidebarRef={sidebarRef}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        isActive={isActive}
        setIsActive={setIsActive}
      />

      {isModal && userTableInfo && (
        <TimeExtendModal
          isOpen={isModal}
          toggle={toggleModal}
          currentUserTable={currentUserTable}
          restaurantsDetail={userTableInfo}
          onExtend={() => handleTimeAction(ExtendTime)}
          onEnd={() => handleTimeAction(EndTime)}
        />
      )}
    </div>
  );
};

export default Header;
